import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Video from "../../../common/Video";
import {
  ModuleCopyBlock,
  ModuleCopyBlock2,
  ModuleCopyBody,
  ModuleCopyHeadline,
  ModuleSection
} from "./Parts";

import { gsap } from "../../../gsap";
import { useGSAP } from "../../../gsap/react";

import { useUserContext } from "../../../context/UserContext";
import { triggerGAEvent } from "../../../helpers";

const FlyThrough = ({
  className,
  headline,
  bodyCopy,
  videoName,
  link = null,
  linkId = null,
  media,
  imgSrc = null,
  imgSrcMobile = null,
  videoHeight,
  id,
  headlineId,
  bodyId,
  bodyClass,
  gradientTopper = false,
  gradientTopperWelcome = false,
  disclaimer,
  gaTriggerEvent,
  animation = true,
  welcome = false,
  learnMoreButton = false,
  gradient = true,
  tallGradient = false,
  showIcon = false,
  iconSrc,
  icon2Src,
  fullscreen = true,
  isMobileRender = false,
}) => {
  const panTl = useRef();
  const { isTrade, allVideosPlaying } = useUserContext();

  useGSAP(() => {
    panTl.current = gsap
      .timeline({
        scrollTrigger: {
          start: "top center",
          trigger: ".flyover-image"
          // markers: true
        },
        repeat: -1,
        yoyo: true
      })
      .set(".flyover-image", { backgroundPosition: "50% 50%" })
      .to(".flyover-image", { scale: 1, duration: 10 })
      .to(".flyover-image", { backgroundPosition: "10% 50%", duration: 10 }, "<")
      .to(".flyover-image", { backgroundPosition: "90% 50%", duration: 10 }, ">-=1");
  }, {});

  useEffect(() => {
    if (!allVideosPlaying) {
      panTl.current.kill();
    } else {
      panTl.current.restart();
    }
  });



  const mobileClass = !animation ? " flyover-image-m" : " flyover-image";

  const bkgRender = isMobileRender ? imgSrcMobile : imgSrc;

  return (
    <ModuleSection fullscreen={fullscreen} className={className}>
      {media === "video" ? (
        <div className="-z-[10] flyover-module h-full">
          {" "}
          <Video
            id={id}
            isPlaying={true}
            height={videoHeight}
            poster={videoName}
            name={videoName}
            autoplay={allVideosPlaying}
            videoplaying={allVideosPlaying}
            gradientTopper={true}
          ></Video>
        </div>
      ) : (
        <div
          className={`absolute h-full w-full bg-cover bg-top ${mobileClass}`}
          style={{ backgroundImage: "url(" + bkgRender + ".jpg?v=2)" }}
        >
          {" "}
          {gradient ? (
            <div
              className={`absolute w-full h-[20%] lg:h-[50%] bg-gradient-to-t from-dark-blue -z-[1] -bottom-[5px]`}
            ></div>
          ) : (
            <div></div>
          )}
        </div>
      )}
      <div className="my-[16px] lg:my-0 ">
        {" "}
        {gradientTopper && (
          <div className="absolute w-full h-[25%] z-20 top-[26%] bg-gradient-to-t from-dark-blue to-transparent lg:hidden"></div>
        )}
        {welcome ? (
          <ModuleCopyBlock2
            className={
              gradientTopperWelcome && `z-30 lg:z-30 ` + bodyClass + ` lg:bg-transparent  `
            }
          >
            <ModuleCopyHeadline id={headlineId} theme={`light`}>
              {headline}
            </ModuleCopyHeadline>
            <>
              <ModuleCopyBody id={bodyId} theme={`light`}>
                <span dangerouslySetInnerHTML={{ __html: bodyCopy }}></span>
                <small className="mt-3 block">{disclaimer}</small>
              </ModuleCopyBody>
              <div id={linkId} className={`mt-3 cursor-pointer text-white underline  text-[19px]`}>
                {!isTrade && link ? (
                  <a
                    href="https://www.carnival.com/cruise-search?pageNumber=1&numadults=2&ptport=CBK&pagesize=8&sort=fromprice&showBest=true&async=true&currency=USD&locality=1"
                    onClick={() => {
                      triggerGAEvent(gaTriggerEvent);
                    }}
                  >
                    {link}
                  </a>
                ) : null}
              </div>
            </>
          </ModuleCopyBlock2>
        ) : (
          <ModuleCopyBlock
            gradient={gradient}
            tallGradient={tallGradient}
            className={`${
              gradientTopper && `z-10 lg:z-30 ` + bodyClass + ` bg-dark-blue lg:bg-transparent `
            }`}
          >
            <ModuleCopyHeadline id={headlineId} theme={`light`}>
              <div className="flex flex-row max-w-[350px] mx-auto">
                {showIcon && (
                  <img
                    alt=""
                    className="block mx-auto mb-3 max-w-[128px] lg:max-w-[165px]"
                    src={iconSrc}
                  />
                )}
                {icon2Src && (
                  <img
                    alt=""
                    className="block mx-auto mb-3 max-w-[128px] lg:max-w-[150px]"
                    src={icon2Src}
                  />
                )}
              </div>

              {headline}
            </ModuleCopyHeadline>
            <>
              <ModuleCopyBody id={bodyId} theme={`light`}>
                {bodyCopy}
                {learnMoreButton && (
                  <a
                    href="https://www.carnival.com/shore-excursions/celebration-key?icid=icp_ccl_ck_microsite_shorex"
                    onClick={() => {
                      triggerGAEvent("pp_book-now");
                    }}
                    aria-label="Book now"
                    class="bg-white font-instrument text-dark-blue block py-3 mx-auto px-10 lg:px-20 mt-4 text-[20px] font-bold h-12 leading-none max-w-[292px]"
                  >
                    Book Now
                  </a>
                )}
                <small className="mt-3 block">{disclaimer}</small>
              </ModuleCopyBody>
              <div id={linkId} className={`mt-3 cursor-pointer text-white underline text-[19px]`}>
                {!isTrade && link ? (
                  <a
                    href="https://www.carnival.com/cruise-search?pageNumber=1&numadults=2&ptport=CBK&pagesize=8&sort=fromprice&showBest=true&async=true&currency=USD&locality=1"
                    onClick={() => {
                      triggerGAEvent(gaTriggerEvent);
                    }}
                  >
                    {link}
                  </a>
                ) : null}
              </div>
            </>
          </ModuleCopyBlock>
        )}
      </div>
    </ModuleSection>
  );
};

FlyThrough.propTypes = {
  headline: PropTypes.string.isRequired,
  bodyCopy: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  videoName: PropTypes.string,
  isPlaying: PropTypes.bool.isRequired
};

export default FlyThrough;
